/**
 * Languages: German and English, on equal footing.
 *
 * This product only carries the namespaces for its own pages. The transport
 * wizard under pages/transport-v2/ keeps its own separate catalog and its
 * own language toggle; the two agree only on load, through the shared
 * iio-lang localStorage key.
 *
 * Built on i18next with react-i18next, the common choice for React. It
 * provides exactly what is needed here: namespaces per page, placeholders,
 * plural forms, and a single place to hold the current language.
 *
 * Layout:
 *   src/i18n/index.js       this file, setup and language switching
 *   src/i18n/de/<ns>.json   German text per page
 *   src/i18n/en/<ns>.json   English text per page
 *
 * Adding a new page:
 *   1. Create two files: de/mypage.json and en/mypage.json
 *   2. Register both below in KATALOG
 *   3. In the page: const { t } = useTranslation("mypage");
 *      then use t("title") instead of a fixed string
 *
 * The key is the address of the text, not the text itself, so it stays
 * stable when the wording changes.
 *
 * German is not a translation of English. Both versions are written
 * independently. German labels typically run a fifth to a third longer than
 * their English counterparts, which rules out fixed widths sized for one
 * language.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Catalog: static imports instead of lazy loading. The app sits behind a
// login, the text volumes are small, and lazy loading would only add a
// second failure mode without a noticeable benefit.
import deGemeinsam from "./de/gemeinsam.json";
import enGemeinsam from "./en/gemeinsam.json";
import deNavigation from "./de/navigation.json";
import enNavigation from "./en/navigation.json";
import deSpaces from "./de/spaces.json";
import enSpaces from "./en/spaces.json";
import deEinstellungen from "./de/einstellungen.json";
import enEinstellungen from "./en/einstellungen.json";
import deSpace from "./de/space.json";
import enSpace from "./en/space.json";
import deCicdSetup from "./de/cicdsetup.json";
import enCicdSetup from "./en/cicdsetup.json";
import deCicdMonitor from "./de/cicdmonitor.json";
import enCicdMonitor from "./en/cicdmonitor.json";
import deFreigabe from "./de/freigabe.json";
import enFreigabe from "./en/freigabe.json";
import deSchleuse from "./de/schleuse.json";
import enSchleuse from "./en/schleuse.json";
import deBenutzer from "./de/benutzer.json";
import enBenutzer from "./en/benutzer.json";

const KATALOG = {
  de: {
    gemeinsam: deGemeinsam,
    navigation: deNavigation,
    spaces: deSpaces,
    einstellungen: deEinstellungen,
    space: deSpace,
    cicdsetup: deCicdSetup,
    cicdmonitor: deCicdMonitor,
    freigabe: deFreigabe,
    schleuse: deSchleuse,
    benutzer: deBenutzer,
  },
  en: {
    gemeinsam: enGemeinsam,
    navigation: enNavigation,
    spaces: enSpaces,
    einstellungen: enEinstellungen,
    space: enSpace,
    cicdsetup: enCicdSetup,
    cicdmonitor: enCicdMonitor,
    freigabe: enFreigabe,
    schleuse: enSchleuse,
    benutzer: enBenutzer,
  },
};

export const SPRACHEN = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
];

// The same key the transport wizard has always used, so both areas start
// from the same saved language on load rather than drifting apart.
export const SPEICHER_SCHLUESSEL = "iio-lang";

function startSprache() {
  try {
    const gemerkt = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (gemerkt === "de" || gemerkt === "en") return gemerkt;
  } catch { /* private browsing without storage: fall back to the default */ }
  const vomBrowser = (navigator.language || "de").slice(0, 2).toLowerCase();
  return vomBrowser === "en" ? "en" : "de";
}

i18n.use(initReactI18next).init({
  resources: KATALOG,
  lng: startSprache(),
  fallbackLng: "de",
  defaultNS: "gemeinsam",
  interpolation: {
    escapeValue: false, // React already escapes against injection
  },
  returnEmptyString: false,
  // A missing key renders as the key itself, which is obvious in a visual
  // check instead of silently leaving a blank area.
});

/**
 * Switch the language and remember the choice.
 * @param {"de"|"en"} code
 */
export function setzeSprache(code) {
  if (code !== "de" && code !== "en") return;
  i18n.changeLanguage(code);
  // helpers.js reads this for the Accept-Language header it sends along.
  if (typeof window !== "undefined") window.__iioLang = code;
  try { localStorage.setItem(SPEICHER_SCHLUESSEL, code); } catch { /* see above */ }
  // Broadcast so any other module that keeps its own catalog, such as the
  // transport wizard, can pick this up if it chooses to listen.
  window.dispatchEvent(new CustomEvent("iio-lang-change", { detail: code }));
  document.documentElement.setAttribute("lang", code);
}

// Picks up a language change dispatched by another module, should one ever
// send it. Does not re-dispatch here, which would otherwise create a loop.
window.addEventListener("iio-lang-change", (e) => {
  const code = e && e.detail;
  if (code !== "de" && code !== "en") return;
  if (i18n.language === code) return;
  i18n.changeLanguage(code);
  document.documentElement.setAttribute("lang", code);
});

document.documentElement.setAttribute("lang", i18n.language);
if (typeof window !== "undefined") window.__iioLang = i18n.language;

export default i18n;
